import { AbsolutePath, type Schema, type SchemaKt } from "@ostack.tech/kform";
import {
  type ComponentPropsWithoutRef,
  type ComponentRef,
  type ForwardedRef,
  forwardRef,
  useMemo,
} from "react";

import { FormContext } from "../contexts/FormContext.ts";
import { type FormOptions, useForm } from "../hooks/useForm.ts";

/** Properties of the {@link Form} component. */
export type FormProps<T = unknown, TSubmitResult = unknown> = FormOptions<
  T,
  TSubmitResult
> &
  FormOwnProps<T> &
  ComponentPropsWithoutRef<"form">;

/** Own properties of the {@link Form} component. */
export interface FormOwnProps<T = unknown> {
  /** Schema of the form. */
  schema: Schema<T> | SchemaKt;
}

/**
 * Component exposing a form managed by a [form manager]{@link FormManager},
 * initialised with the provided schema.
 */
export const Form = forwardRef(function Form<
  T = unknown,
  TSubmitResult = unknown,
>(
  {
    schema,
    initialValue,
    enabled,
    _defaultState,
    onInitialized,
    onUninitialized,
    onFormManagerEvent,
    onValueChange,
    onValidationStatusChange,
    onDisplayStatusChange,
    onDirtyStatusChange,
    onTouchedStatusChange,
    externalContexts,
    validationMode,
    confirmUnloadWhenDirty,
    confirmUnloadMessage,
    onSubmit,
    onInvalidSubmit,
    onSuccessfulSubmit,
    onFailedSubmit,
    setTouchedOnSubmit,
    validateOnSubmit,
    setPristineOnSuccessfulSubmit,
    convertExternalIssuesTableRowIndicesToIds,
    onReset,
    ...otherProps
  }: FormProps<T, TSubmitResult>,
  forwardedRef: ForwardedRef<ComponentRef<"form">>,
) {
  const formController = useForm<T, TSubmitResult>(schema, {
    initialValue,
    enabled,
    _defaultState,
    onInitialized,
    onUninitialized,
    onFormManagerEvent,
    onValueChange,
    onValidationStatusChange,
    onDisplayStatusChange,
    onDirtyStatusChange,
    onTouchedStatusChange,
    externalContexts,
    validationMode,
    confirmUnloadWhenDirty,
    confirmUnloadMessage,
    onSubmit,
    onInvalidSubmit,
    onSuccessfulSubmit,
    onFailedSubmit,
    setTouchedOnSubmit,
    validateOnSubmit,
    setPristineOnSuccessfulSubmit,
    convertExternalIssuesTableRowIndicesToIds,
    onReset,
  });

  const formManager = formController.useFormManager();
  const formContext = useMemo(
    () => ({
      formManager,
      currentPath: AbsolutePath.ROOT,
      controller: formController,
    }),
    [formController, formManager],
  );

  return (
    <FormContext.Provider value={formContext}>
      <form {...formController.formProps} {...otherProps} ref={forwardedRef} />
    </FormContext.Provider>
  );
});
